
import { useState } from 'react';

const LessonCategories = () => {
  const [selectedCategory, setSelectedCategory] = useState('grammar');
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [reviewLesson, setReviewLesson] = useState<any>(null);
  const [currentReviewQuestion, setCurrentReviewQuestion] = useState(0);
  const [userAnswer, setUserAnswer] = useState('');
  const [showResult, setShowResult] = useState(false);
  const [reviewScore, setReviewScore] = useState(0);

  const categories = [
    {
      id: 'grammar',
      name: 'Grammar',
      icon: 'ri-book-line',
      color: 'blue',
      description: 'Master English grammar rules with clear explanations and examples',
      lessons: [
        { 
          title: 'Present Simple vs Present Continuous', 
          level: 'Beginner', 
          completed: true,
          reviewQuestions: [
            {
              question: 'Choose the correct form: "I _____ coffee every morning."',
              options: ['drink', 'am drinking', 'drinks', 'drinking'],
              correct: 'drink',
              explanation: 'We use Present Simple for daily habits and routines.'
            },
            {
              question: 'What is correct: "She _____ TV right now."',
              options: ['watches', 'is watching', 'watch', 'watching'],
              correct: 'is watching',
              explanation: 'We use Present Continuous for actions happening right now.'
            }
          ]
        },
        { 
          title: 'Past Tense Forms', 
          level: 'Beginner', 
          completed: true,
          reviewQuestions: [
            {
              question: 'Complete: "Yesterday I _____ to the park."',
              options: ['go', 'went', 'going', 'goes'],
              correct: 'went',
              explanation: 'We use past tense "went" for completed actions in the past.'
            },
            {
              question: 'Which is correct: "They _____ the movie last night."',
              options: ['watch', 'watched', 'watching', 'watches'],
              correct: 'watched',
              explanation: 'Regular verbs add -ed to form the past tense.'
            }
          ]
        },
        { title: 'Present Perfect Tense', level: 'Intermediate', completed: false, current: true },
        { title: 'Future Tenses', level: 'Intermediate', completed: false },
        { title: 'Conditional Sentences', level: 'Advanced', completed: false },
      ]
    },
    {
      id: 'vocabulary',
      name: 'Vocabulary',
      icon: 'ri-chat-3-line',
      color: 'green',
      description: 'Build your word power with practical vocabulary for everyday situations',
      lessons: [
        { 
          title: 'Daily Routines & Activities', 
          level: 'Beginner', 
          completed: true,
          reviewQuestions: [
            {
              question: 'What do you do when you "brush your teeth"?',
              options: ['Clean your teeth', 'Wash your hair', 'Take a shower', 'Eat breakfast'],
              correct: 'Clean your teeth',
              explanation: 'Brushing teeth means cleaning your teeth with a toothbrush.'
            },
            {
              question: 'When do you usually "have breakfast"?',
              options: ['Evening', 'Afternoon', 'Morning', 'Night'],
              correct: 'Morning',
              explanation: 'Breakfast is the first meal of the day, eaten in the morning.'
            }
          ]
        },
        { 
          title: 'Food & Cooking', 
          level: 'Beginner', 
          completed: true,
          reviewQuestions: [
            {
              question: 'What does "boil" mean in cooking?',
              options: ['Cook in hot oil', 'Cook in hot water', 'Cook in oven', 'Cook on grill'],
              correct: 'Cook in hot water',
              explanation: 'Boiling means cooking food in very hot water (100°C).'
            }
          ]
        },
        { title: 'Travel & Transportation', level: 'Intermediate', completed: false },
        { title: 'Business English Basics', level: 'Intermediate', completed: false },
        { title: 'American Slang & Expressions', level: 'Advanced', completed: false },
      ]
    },
    {
      id: 'pronunciation',
      name: 'Pronunciation',
      icon: 'ri-mic-line',
      color: 'purple',
      description: 'Perfect your pronunciation with audio exercises and speaking practice',
      lessons: [
        { 
          title: 'English Sounds & Phonetics', 
          level: 'Beginner', 
          completed: true,
          reviewQuestions: [
            {
              question: 'Which word has the /θ/ sound?',
              options: ['think', 'sink', 'drink', 'link'],
              correct: 'think',
              explanation: 'The /θ/ sound is the "th" sound in words like "think", "three", "thank".'
            }
          ]
        },
        { title: 'Word Stress Patterns', level: 'Beginner', completed: false },
        { title: 'Sentence Rhythm & Intonation', level: 'Intermediate', completed: false },
        { title: 'Difficult Sounds for Non-Native Speakers', level: 'Intermediate', completed: false },
        { title: 'American Accent Training', level: 'Advanced', completed: false },
      ]
    },
    {
      id: 'listening',
      name: 'Listening',
      icon: 'ri-headphone-line',
      color: 'orange',
      description: 'Improve your listening skills with real conversations and audio content',
      lessons: [
        { 
          title: 'Basic Conversations', 
          level: 'Beginner', 
          completed: true,
          reviewQuestions: [
            {
              question: 'What is a common greeting in English?',
              options: ['How are you?', 'Where are you?', 'What are you?', 'Why are you?'],
              correct: 'How are you?',
              explanation: '"How are you?" is a standard greeting to ask about someone\'s well-being.'
            }
          ]
        },
        { title: 'Phone Conversations', level: 'Beginner', completed: false },
        { title: 'News & Media Listening', level: 'Intermediate', completed: false },
        { title: 'Movies & TV Shows', level: 'Intermediate', completed: false },
        { title: 'Podcasts & Interviews', level: 'Advanced', completed: false },
      ]
    }
  ];

  const getColorClasses = (color: string, variant: 'bg' | 'text' | 'border') => {
    const colorMap = {
      blue: { bg: 'bg-blue-500', text: 'text-blue-600', border: 'border-blue-500' },
      green: { bg: 'bg-green-500', text: 'text-green-600', border: 'border-green-500' },
      purple: { bg: 'bg-purple-500', text: 'text-purple-600', border: 'border-purple-500' },
      orange: { bg: 'bg-orange-500', text: 'text-orange-600', border: 'border-orange-500' },
    };
    return colorMap[color as keyof typeof colorMap][variant];
  };

  const handleReviewClick = (lesson: any, categoryColor: string) => {
    if (lesson.reviewQuestions && lesson.reviewQuestions.length > 0) {
      setReviewLesson({ ...lesson, categoryColor });
      setShowReviewModal(true);
      setCurrentReviewQuestion(0);
      setUserAnswer('');
      setShowResult(false);
      setReviewScore(0);
    }
  };

  const handleReviewAnswer = (answer: string) => {
    setUserAnswer(answer);
    setShowResult(true);
    if (answer === reviewLesson.reviewQuestions[currentReviewQuestion].correct) {
      setReviewScore(reviewScore + 1);
    }
  };

  const nextReviewQuestion = () => {
    if (currentReviewQuestion < reviewLesson.reviewQuestions.length - 1) {
      setCurrentReviewQuestion(currentReviewQuestion + 1);
      setUserAnswer('');
      setShowResult(false);
    } else {
      // Review completed
      const finalScore = reviewScore + (userAnswer === reviewLesson.reviewQuestions[currentReviewQuestion].correct ? 1 : 0);
      alert(`Review completed! Your score: ${finalScore}/${reviewLesson.reviewQuestions.length}`);
      setShowReviewModal(false);
      setReviewLesson(null);
      setCurrentReviewQuestion(0);
      setReviewScore(0);
      setUserAnswer('');
      setShowResult(false);
    }
  };

  return (
    <>
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Choose Your Learning Path
              </h2>
              <p className="text-xl text-gray-600">
                Select a category and start practicing with interactive exercises designed just for you
              </p>
            </div>

            {/* Category Tabs */}
            <div className="flex flex-wrap justify-center gap-4 mb-12">
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`flex items-center space-x-2 px-6 py-3 rounded-lg font-semibold transition-all whitespace-nowrap cursor-pointer ${
                    selectedCategory === category.id
                      ? `${getColorClasses(category.color, 'bg')} text-white shadow-lg`
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <i className={`${category.icon} text-lg`}></i>
                  <span>{category.name}</span>
                </button>
              ))}
            </div>

            {/* Selected Category Content */}
            {categories.map((category) => (
              selectedCategory === category.id && (
                <div key={category.id} className="bg-gray-50 rounded-2xl p-8">
                  <div className="text-center mb-8">
                    <div className={`w-16 h-16 ${getColorClasses(category.color, 'bg')} rounded-full flex items-center justify-center mx-auto mb-4`}>
                      <i className={`${category.icon} text-white text-2xl`}></i>
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">{category.name}</h3>
                    <p className="text-gray-600">{category.description}</p>
                  </div>

                  {/* Lessons List */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {category.lessons.map((lesson, index) => (
                      <div
                        key={index}
                        className={`bg-white rounded-xl p-6 shadow-sm border-2 transition-all cursor-pointer hover:shadow-md ${
                          lesson.current ? `${getColorClasses(category.color, 'border')} ring-2 ring-opacity-20` : 'border-gray-200'
                        }`}
                      >
                        <div className="flex items-start justify-between mb-4">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                            lesson.completed ? 'bg-green-100' : lesson.current ? `${getColorClasses(category.color, 'bg')}/10` : 'bg-gray-100'
                          }`}>
                            {lesson.completed ? (
                              <i className="ri-checkbox-circle-fill text-green-600 text-lg"></i>
                            ) : lesson.current ? (
                              <i className={`ri-play-circle-line ${getColorClasses(category.color, 'text')} text-lg`}></i>
                            ) : (
                              <i className="ri-lock-line text-gray-400 text-lg"></i>
                            )}
                          </div>
                          <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                            lesson.level === 'Beginner' ? 'bg-green-100 text-green-700' :
                            lesson.level === 'Intermediate' ? 'bg-yellow-100 text-yellow-700' :
                            'bg-red-100 text-red-700'
                          }`}>
                            {lesson.level}
                          </span>
                        </div>
                        
                        <h4 className="font-semibold text-gray-900 mb-2">{lesson.title}</h4>
                        
                        <button 
                          onClick={() => lesson.completed && handleReviewClick(lesson, category.color)}
                          className={`w-full py-2 px-4 rounded-lg font-medium transition-colors whitespace-nowrap cursor-pointer ${
                            lesson.completed ? 'bg-green-100 text-green-700 hover:bg-green-200' :
                            lesson.current ? `${getColorClasses(category.color, 'bg')} text-white hover:opacity-90` :
                            'bg-gray-100 text-gray-500 cursor-not-allowed'
                          }`}
                        >
                          {lesson.completed ? 'Review' : lesson.current ? 'Continue' : 'Locked'}
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )
            ))}
          </div>
        </div>
      </section>

      {/* Review Modal */}
      {showReviewModal && reviewLesson && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-2xl font-bold text-gray-900">Review: {reviewLesson.title}</h3>
                <button
                  onClick={() => setShowReviewModal(false)}
                  className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer"
                >
                  <i className="ri-close-line text-xl"></i>
                </button>
              </div>
              <p className="text-gray-600 mt-2">
                Question {currentReviewQuestion + 1} of {reviewLesson.reviewQuestions.length}
              </p>
            </div>

            <div className="p-6">
              {/* Progress Bar */}
              <div className="w-full bg-gray-200 rounded-full h-2 mb-6">
                <div
                  className={`h-2 rounded-full transition-all duration-300 ${getColorClasses(reviewLesson.categoryColor, 'bg')}`}
                  style={{ width: `${((currentReviewQuestion + 1) / reviewLesson.reviewQuestions.length) * 100}%` }}
                ></div>
              </div>

              {/* Question */}
              <div className="mb-6">
                <h4 className="text-xl font-semibold text-gray-900 mb-6">
                  {reviewLesson.reviewQuestions[currentReviewQuestion].question}
                </h4>
                
                {/* Answer Options */}
                <div className="grid grid-cols-1 gap-3">
                  {reviewLesson.reviewQuestions[currentReviewQuestion].options.map((option: string, index: number) => (
                    <button
                      key={index}
                      onClick={() => !showResult && handleReviewAnswer(option)}
                      disabled={showResult}
                      className={`p-4 rounded-lg border-2 text-left transition-all cursor-pointer ${
                        showResult
                          ? option === reviewLesson.reviewQuestions[currentReviewQuestion].correct
                            ? 'border-green-500 bg-green-50 text-green-700'
                            : option === userAnswer && option !== reviewLesson.reviewQuestions[currentReviewQuestion].correct
                            ? 'border-red-500 bg-red-50 text-red-700'
                            : 'border-gray-200 bg-gray-50 text-gray-500'
                          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      <span className="font-medium">{String.fromCharCode(65 + index)}.</span> {option}
                    </button>
                  ))}
                </div>
              </div>

              {/* Result and Explanation */}
              {showResult && (
                <div className={`p-4 rounded-lg mb-6 ${
                  userAnswer === reviewLesson.reviewQuestions[currentReviewQuestion].correct 
                    ? 'bg-green-50 border border-green-200' 
                    : 'bg-red-50 border border-red-200'
                }`}>
                  <div className="flex items-center mb-3">
                    <i className={`${
                      userAnswer === reviewLesson.reviewQuestions[currentReviewQuestion].correct 
                        ? 'ri-checkbox-circle-fill text-green-600' 
                        : 'ri-close-circle-fill text-red-600'
                    } text-xl mr-2`}></i>
                    <span className={`font-semibold ${
                      userAnswer === reviewLesson.reviewQuestions[currentReviewQuestion].correct 
                        ? 'text-green-700' 
                        : 'text-red-700'
                    }`}>
                      {userAnswer === reviewLesson.reviewQuestions[currentReviewQuestion].correct ? 'Correct!' : 'Incorrect'}
                    </span>
                  </div>
                  <p className="text-gray-700">{reviewLesson.reviewQuestions[currentReviewQuestion].explanation}</p>
                </div>
              )}

              {/* Next Button */}
              {showResult && (
                <div className="text-center">
                  <button
                    onClick={nextReviewQuestion}
                    className={`${getColorClasses(reviewLesson.categoryColor, 'bg')} text-white px-8 py-3 rounded-lg font-semibold hover:opacity-90 transition-colors whitespace-nowrap cursor-pointer`}
                  >
                    {currentReviewQuestion < reviewLesson.reviewQuestions.length - 1 ? 'Next Question' : 'Complete Review'}
                    <i className="ri-arrow-right-line ml-2"></i>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default LessonCategories;
